import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import { CompleteIcon } from 'shared-components';

import SectionStartTemplate from '../../templates/sectionStart';
import { SectionStatus } from '../../atoms/section-summary';
import { titleAppend } from '../../../utilities/head';
import { Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import { createNavigateBackwardAction, createNavigateToAction } from '../../../middleware/actions';
import { toggleTaskMenu } from '../../../store/ui/actions';
import * as types from 'types';

const AllCompleteSection = () => {
  const personalStarted = useSelector((state: ApplicationState) => state.student.offer.isPISectionStarted);
  const historyStarted = useSelector((state: ApplicationState) => state.student.offer.isHSectionStarted);
  const consentsStarted = useSelector((state: ApplicationState) => state.student.consents.isSectionStarted);
  const financeStarted = useSelector((state: ApplicationState) => state.student.financials.isSectionStarted);

  const historySectionStatus = useSelector((state: ApplicationState) => state.config.historySectionStatus);
  const financeSectionStatus = useSelector((state: ApplicationState) => state.config.financeSectionStatus);
  const historyInactive = historySectionStatus === types.SectionStatusType.INACTIVE;
  const financeInactive = financeSectionStatus === types.SectionStatusType.INACTIVE;

  const personalComplete = useSelector((state: ApplicationState) => state.student.offer.isPISectionComplete);
  const historyComplete = useSelector((state: ApplicationState) => state.student.offer.isHSectionComplete);
  const consentsComplete = useSelector((state: ApplicationState) => state.student.consents.isSectionComplete);
  const financeComplete = useSelector((state: ApplicationState) => state.student.financials.isSectionComplete);
  const warning = useSelector((state: ApplicationState) => state.config.Checklist.Summary.Complete.warning);
  const completeTitle = useSelector((state: ApplicationState) => state.config.Checklist.Summary.Complete.title);
  const incompleteTitle = useSelector((state: ApplicationState) => state.config.Checklist.Summary.Incomplete.title);

  const [isTaskCompleted, setIsTaskCompleted] = useState<boolean>(false);

  const dispatch = useDispatch();
  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = () => dispatch(createNavigateToAction('/dashboard'));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  const personalInfoStatus = (() => {
    if (personalComplete) return SectionStatus.complete;
    if (personalStarted) return SectionStatus.incomplete;
    return SectionStatus.empty;
  })();

  const historyStatus = (() => {
    if (historyInactive) return SectionStatus.skipped;
    if (historyComplete) return SectionStatus.complete;
    if (historyStarted) return SectionStatus.incomplete;
    return SectionStatus.empty;
  })();

  const consentsStatus = (() => {
    if (consentsComplete) return SectionStatus.complete;
    if (consentsStarted) return SectionStatus.incomplete;
    return SectionStatus.empty;
  })();

  const financeStatus = (() => {
    if (financeInactive) return SectionStatus.skipped;
    if (financeComplete) return SectionStatus.complete;
    if (financeStarted) return SectionStatus.incomplete;
    return SectionStatus.empty;
  })();

  const completed =
    consentsStatus === SectionStatus.complete &&
    [SectionStatus.complete, SectionStatus.skipped].includes(financeStatus) &&
    [SectionStatus.complete, SectionStatus.skipped].includes(historyStatus) &&
    personalInfoStatus === SectionStatus.complete;

  useEffect(() => {
    setIsTaskCompleted(completed);
  }, [completed]);

  return (
    <SectionStartTemplate
      pageHeader={isTaskCompleted ? completeTitle : incompleteTitle}
      section1={personalInfoStatus}
      section2={historyStatus}
      section3={consentsStatus}
      section4={financeStatus}
      onNext={onNext}
      nextLabel={isTaskCompleted ? 'Go to task list' : 'Go to unanswered questions'}
      onPrevious={onPrevious}
      onBackToTaskList={onBackToTaskList}
    >
      <Helmet>
        <title>Section complete {titleAppend}</title>
      </Helmet>

      {isTaskCompleted ? (
        <>
          <CompleteIcon />

          <Text fontSize="lg">{warning}</Text>
        </>
      ) : (
        <>
          <CompleteIcon isIncomplete />

          <Text fontSize="lg">
            You still have <b>unanswered questions</b>, you will need to answer them to proceed.
          </Text>

          <Text fontSize="lg">Would you like to answer them now?</Text>
        </>
      )}
    </SectionStartTemplate>
  );
};

export default AllCompleteSection;
